import { useMemo, useRef } from 'react';
import {
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { enqueueSnackbar } from 'notistack';
import { MapProvider } from 'shared/map-exports';
import { Map } from 'features/mapping/components/Map';
import { MapType } from 'features/mapping/mapTypePolicy';
import {
  ActiveFirePerimetersLayer,
  EditableFirePerimeterLayer,
} from 'features/mapping/components/layers/FirePerimetersLayer';
import { WildfiresLayer } from 'features/mapping/components/layers/WildfiresLayer';
import { useAllGeoEvents } from 'features/geoEvents/hooks/useAllGeoEvents';
import { useGeoEventAutocomplete } from 'features/geoEvents/hooks/useGeoEventAutocomplete';
import { MapFormLayout } from 'components/MapFormLayout';
import { FormWithoutEnter } from 'components/FormWithoutEnter';
import { AutocompleteWithBackdrop } from 'components/AutocompleteWithBackdrop';
import { useDefaultMapCenter } from 'hooks/useDefaultMapCenter';
import { formatNumber, getDisplayValueForKey } from 'shared/utils';
import { useZoomFromQueryParams } from 'hooks/useZoomFromQueryParams';
import { useIosKeyboardScroller } from 'hooks/useIosKeyboardScroller';
import { useFormBlocker } from 'hooks/useFormBlocker';
import { formatTimestamp } from 'shared/dates';
import { ZOOM_LEVELS } from 'features/mapping/mapping.constants';
import {
  FirePerimeter,
  FirePerimeterFormValues,
  FirePerimeterGeoEvent,
} from './FirePerimeterForm.types';
import {
  approvalStatusOptions,
  formValuesToPatch,
  getEffectivePerimeterType,
  getInitialValues,
  getPerimeterPreviewColor,
  getValidationSchema,
  useFirePerimeter,
} from './FirePerimeterForm.utils';

type FirePerimeterFormProps = {
  firePerimeter: FirePerimeter;
};

export const FirePerimeterForm = (
  props: FirePerimeterFormProps,
): JSX.Element => {
  const { firePerimeter } = props;
  const { t } = useTranslation();

  const { wildfireEvents } = useAllGeoEvents();

  const initialZoom = useZoomFromQueryParams() ?? ZOOM_LEVELS.CITY;
  const defaultMapCenter = useDefaultMapCenter({
    geoEvent: firePerimeter.center ?? undefined,
  });

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  useIosKeyboardScroller(scrollContainerRef);

  const initialValues = useMemo(
    () => getInitialValues(firePerimeter),
    [firePerimeter],
  );

  const validationSchema = useMemo(() => getValidationSchema(t), [t]);

  const formMethods = useForm<FirePerimeterFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
    values: initialValues,
    mode: 'onBlur',
  });

  const { control, handleSubmit, formState, watch } = formMethods;

  const { unblockedNavigate } = useFormBlocker({
    shouldBlockNavigation: Boolean(formState.isDirty),
  });

  const [approvalStatus, geoEvent] = watch(['approvalStatus', 'geoEvent']);
  const isApproved = approvalStatus === 'approved';
  const previewColor = getPerimeterPreviewColor(
    approvalStatus,
    getEffectivePerimeterType(geoEvent, firePerimeter),
  );

  const { muiAutocompleteProps: geoEventAutocompleteProps } =
    useGeoEventAutocomplete<FirePerimeterGeoEvent>({
      enabled: isApproved,
      value: geoEvent,
      geoEventTypes: ['wildfire'],
      multiple: false,
    });

  const { updateFirePerimeter } = useFirePerimeter(firePerimeter.id);

  const handleSubmitFirePerimeter: SubmitHandler<
    FirePerimeterFormValues
  > = async (values) => {
    const patch = formValuesToPatch(values, firePerimeter);

    const navigateAfterSave = (): void => {
      unblockedNavigate(values.geoEvent ? `/i/${values.geoEvent.id}` : '/');
    };

    // Nothing changed - just leave the form.
    if (Object.keys(patch).length === 0) {
      navigateAfterSave();
      return;
    }

    const { errorMessage } = await updateFirePerimeter(patch);

    if (errorMessage) {
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return;
    }

    enqueueSnackbar(t('editFirePerimeter.successMessage'), {
      variant: 'success',
    });

    navigateAfterSave();
  };

  const readOnlyFieldProps: Pick<TextFieldProps, 'disabled' | 'fullWidth'> = {
    disabled: true,
    fullWidth: true,
  };

  return (
    <MapProvider>
      <MapFormLayout ref={scrollContainerRef}>
        <MapFormLayout.MapContent>
          <Map
            center={defaultMapCenter}
            zoom={initialZoom}
            mapType={MapType.FirePerimeterForm}
          >
            <ActiveFirePerimetersLayer
              visible
              interactive={false}
              excludedPerimeterId={firePerimeter.id}
              layerIdPrefix="edit-fire-perimeter-"
              mode="review"
            />
            <EditableFirePerimeterLayer
              geom={firePerimeter.geom}
              color={previewColor}
              activePerimetersDelta={firePerimeter.activePerimetersDelta}
              activePerimetersDeltaStroke={
                firePerimeter.activePerimetersDeltaStroke
              }
              activePerimetersContraction={
                firePerimeter.activePerimetersContraction
              }
            />
            <WildfiresLayer
              isFadable
              wildfires={wildfireEvents}
              interactive={false}
            />
          </Map>
        </MapFormLayout.MapContent>
        <MapFormLayout.FormContent>
          <FormProvider {...formMethods}>
            <FormWithoutEnter
              noValidate
              onSubmit={handleSubmit(handleSubmitFirePerimeter)}
            >
              <Stack spacing={2}>
                <Typography variant="h3">
                  {t('editFirePerimeter.sections.sourceMetadata')}
                </Typography>

                <TextField
                  {...readOnlyFieldProps}
                  label={t('editFirePerimeter.readOnly.source')}
                  value={firePerimeter.source}
                />
                <TextField
                  {...readOnlyFieldProps}
                  label={t('editFirePerimeter.readOnly.incident')}
                  value={getDisplayValueForKey(
                    firePerimeter,
                    'sourceIncidentName',
                  )}
                />
                <TextField
                  {...readOnlyFieldProps}
                  label={t('editFirePerimeter.readOnly.dateCurrent')}
                  value={getDisplayValueForKey(
                    firePerimeter,
                    'sourceDateCurrent',
                    {
                      format: (value) => formatTimestamp(value.getTime()),
                    },
                  )}
                />
                <TextField
                  {...readOnlyFieldProps}
                  label={t('editFirePerimeter.readOnly.sourceId')}
                  value={firePerimeter.sourceUniqueId}
                />
                <TextField
                  {...readOnlyFieldProps}
                  label={t('editFirePerimeter.readOnly.acres')}
                  value={getDisplayValueForKey(firePerimeter, 'sourceAcres', {
                    format: formatNumber,
                  })}
                />
                <TextField
                  {...readOnlyFieldProps}
                  label={t('editFirePerimeter.readOnly.calculatedAcres')}
                  value={getDisplayValueForKey(
                    firePerimeter,
                    'calculatedAcres',
                    { format: formatNumber },
                  )}
                />
                <TextField
                  {...readOnlyFieldProps}
                  label={t('editFirePerimeter.readOnly.dateCreated')}
                  value={formatTimestamp(firePerimeter.dateCreated.getTime())}
                />
                <TextField
                  {...readOnlyFieldProps}
                  label={t('editFirePerimeter.readOnly.dateModified')}
                  value={formatTimestamp(firePerimeter.dateModified.getTime())}
                />
                <TextField
                  {...readOnlyFieldProps}
                  label={t('editFirePerimeter.readOnly.extra')}
                  value={JSON.stringify(firePerimeter.sourceExtraData ?? {})}
                  multiline
                  maxRows={6}
                />

                <Typography variant="h3">
                  {t('editFirePerimeter.sections.firePerimeter')}
                </Typography>

                <Controller
                  name="approvalStatus"
                  control={control}
                  render={({ field, fieldState }) => {
                    const { ref, ...muiFieldProps } = field;
                    return (
                      <TextField
                        select
                        id="field-control-approval-status"
                        label={t(
                          'editFirePerimeter.inputs.approvalStatus.label',
                        )}
                        fullWidth
                        required
                        {...muiFieldProps}
                        inputRef={ref}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        disabled={formState.isSubmitting}
                      >
                        {approvalStatusOptions.map(({ value, transKey }) => (
                          <MenuItem key={value} value={value}>
                            {t(transKey)}
                          </MenuItem>
                        ))}
                      </TextField>
                    );
                  }}
                />

                <Controller
                  name="geoEvent"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl fullWidth>
                      <AutocompleteWithBackdrop
                        {...geoEventAutocompleteProps}
                        id="geoEvent"
                        disabled={!isApproved || formState.isSubmitting}
                        value={field.value}
                        onChange={(_, value) => field.onChange(value)}
                        onBlur={field.onBlur}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name={field.name}
                            inputRef={field.ref}
                            label={t('editFirePerimeter.inputs.incident.label')}
                            required={isApproved}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </FormControl>
                  )}
                />

                <Button
                  fullWidth
                  size="large"
                  type="submit"
                  disabled={formState.isSubmitting}
                >
                  {formState.isSubmitting ? (
                    <CircularProgress size={24} />
                  ) : (
                    t('common.save')
                  )}
                </Button>
              </Stack>
            </FormWithoutEnter>
          </FormProvider>
        </MapFormLayout.FormContent>
      </MapFormLayout>
    </MapProvider>
  );
};
